import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

import type { LinkedWorkOrderTemplateRef } from "../lib/activity-template-link-error";

export function ActivityDeactivateBlockedDialog({
  open,
  onOpenChange,
  entityLabel,
  templates,
  mode = "deactivate",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityLabel: string;
  templates: LinkedWorkOrderTemplateRef[];
  mode?: "deactivate" | "delete";
}) {
  const label = entityLabel.trim() || "esta actividad";
  const count = templates.length;
  const isDelete = mode === "delete";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle
              className={cn(
                "h-5 w-5",
                isDelete ? "text-destructive" : "text-amber-500",
              )}
            />
            {isDelete ? "No se puede eliminar" : "No se puede desactivar"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isDelete
              ? "La actividad o receta sigue enlazada a flujos de trabajo"
              : "La actividad o receta sigue enlazada a flujos de trabajo"}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div
            className={cn(
              "rounded-md border p-3 text-sm",
              isDelete
                ? "border-destructive/40 bg-destructive/10"
                : "border-amber-500/40 bg-amber-500/10",
            )}
          >
            <div
              className={cn(
                "font-semibold",
                isDelete
                  ? "text-destructive"
                  : "text-amber-700 dark:text-amber-400",
              )}
            >
              {isDelete ? "Eliminación bloqueada" : "Desactivación bloqueada"}
            </div>
            <div className="mt-1 text-muted-foreground">
              {isDelete ? (
                <>
                  «{label}» no puede eliminarse mientras siga en flujos de trabajo.
                  Quitá la actividad de cada flujo de trabajo y guardá antes de borrar.
                </>
              ) : (
                <>
                  «{label}» dejará de estar disponible en el catálogo. Primero
                  debés quitarla de cada flujo de trabajo enlazado y guardar los
                  cambios.
                </>
              )}
            </div>
          </div>

          <div className="rounded-md border bg-muted/10 p-3 text-sm">
            <div className="font-medium">Flujos de trabajo enlazados</div>
            <div className="mt-1 text-muted-foreground">
              {count === 1
                ? "Está asignada a 1 flujo de trabajo:"
                : `Está asignada a ${count} flujos de trabajo:`}
            </div>
            <ul className="mt-2 list-disc pl-5">
              {templates.map((t) => (
                <li key={t.id} className="text-sm">
                  <Link
                    to={`/work-order-templates/${t.id}/edit`}
                    className="text-primary hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    <span className="font-mono text-xs text-muted-foreground">
                      {t.code}
                    </span>{" "}
                    <span>{t.name}</span>
                    {t.status ? (
                      <span className="ml-1 text-muted-foreground">
                        ({t.status})
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator />

        <div className="flex items-center justify-end">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Entendido
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
